/**
 * What the org's lot dates are (B1b T3; GET and PUT /v1/twin/settings/lot-dates, migration 0249
 * org_settings.lot_date_kind): poured (the default), inspected or shipped. Any member reads it; an org admin sets it,
 * and the save queues the analysis job that writes the briefs again with it. The words are lib/brief/lot-dates'.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useBriefLive } from "@/lib/api/brief";
import type { LotDateKind } from "@/lib/brief/lot-dates";
import { errorCodeOf, errorDetailOf, statusOf } from "./error";

/** The org's lot date kind, with the analysis job a change queued (a save only). */
export interface LotDatesSettings {
  lot_date_kind: LotDateKind;
  job?: { id: string };
}

export const LOT_DATES_KEY = ["settings", "lot-dates"] as const;

export const lotDatesApi = {
  get: () => api.get<LotDatesSettings>("/v1/twin/settings/lot-dates"),
  put: (kind: LotDateKind) => api.put<LotDatesSettings>("/v1/twin/settings/lot-dates", { lot_date_kind: kind }),
};

/** The org's lot date kind: any member reads it, on the live platform. */
export function useLotDatesQuery() {
  const live = useBriefLive();
  return useQuery({ queryKey: LOT_DATES_KEY, queryFn: lotDatesApi.get, enabled: live, staleTime: 60_000 });
}

/** Set the lot date kind (an org admin): the answer is the kind as saved. */
export function useSaveLotDates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (kind: LotDateKind) => lotDatesApi.put(kind),
    onSuccess: (saved) => qc.setQueryData(LOT_DATES_KEY, { lot_date_kind: saved.lot_date_kind }),
    onSettled: () => void qc.invalidateQueries({ queryKey: LOT_DATES_KEY }),
  });
}

/** A failed save in plain words. */
export function lotDatesErrorWords(err: unknown): string {
  const code = errorCodeOf(err);
  const status = statusOf(err);
  if (code === "invalid_lot_date_kind") return "Choose poured, inspected or shipped.";
  if (code === "insufficient_permission" || status === 403) return "Only an org admin can change it.";
  if (status !== undefined && status >= 500) return "The platform failed while saving. Read the page again before trying again.";
  return (
    errorDetailOf(err) ??
    (status === undefined ? "The platform did not answer. Try again." : `It could not be saved (HTTP ${status}). Try again.`)
  );
}
